import clsx from 'clsx';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Select as SelectPrimitive } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import type { TSelectBase } from 'types/general.types';
import { EQualifiedState } from 'types/general.types';
import { HubertQualifiedSelectList } from './HubertQualifiedSelect.constants';

interface RootProps extends ComponentProps<typeof SelectPrimitive.Root> {
  valuePlaceholder?: string;
  size?: 'sm' | 'default';
}

const HubertQualifiedSelect = ({ valuePlaceholder = 'Qualified', size, ...props }: RootProps) => {
  console.log('HubertQualifiedSelect', props.value);

  return (
    <SelectPrimitive.Root {...props}>
      <HubertQualifiedSelect.Trigger
        className={clsx(
          'inline-flex outline-none',
          'items-center justify-center',
          size && size === 'sm' ? 'h-[25px]' : 'h-[35px]',
          size && size === 'sm' ? 'px-[10px]' : 'px-[15px]',
          size && size === 'sm' ? 'text-sm' : 'text-md',
          'rounded-md',
          'leading-none',
          'focus:shadow-[0_0_0_1px] focus:shadow-black',
          'data-[placeholder]:text-primary-600',
          props.value && props.value && props.value.length
            ? props.value === EQualifiedState.Qualified
              ? 'bg-success-200 text-success-900'
              : props.value === EQualifiedState.NotQualified
                ? 'bg-danger-200 text-danger-900'
                : 'bg-primary-400 text-primary-50'
            : 'bg-primary-400 text-primary-50',
        )}
      >
        <HubertQualifiedSelect.Value placeholder={valuePlaceholder} aria-label={props.value} />

        <HubertQualifiedSelect.Icon asChild className={clsx(props.disabled && 'hidden')}>
          <ChevronDown className="ml-2 h-4 w-4" />
        </HubertQualifiedSelect.Icon>
      </HubertQualifiedSelect.Trigger>

      <HubertQualifiedSelect.Portal>
        <HubertQualifiedSelect.Content className="overflow-hidden rounded-md bg-white shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)]">
          <HubertQualifiedSelect.ScrollUpButton className="flex h-[25px] cursor-default items-center justify-center bg-white text-black">
            <ChevronUp />
          </HubertQualifiedSelect.ScrollUpButton>
          <HubertQualifiedSelect.Viewport className="p-[5px]">
            {HubertQualifiedSelectList.map((item: TSelectBase, index: number) => {
              return (
                <HubertQualifiedSelect.Item
                  key={`hubert-select-content-${index}`}
                  value={item.value}
                  className={clsx(
                    item.value === EQualifiedState.Qualified ? 'bg-success-200 text-success-900' : '',
                    item.value === EQualifiedState.NotQualified ? 'bg-danger-200 text-danger-900' : '',
                  )}
                >
                  <HubertQualifiedSelect.ItemText>{item.label}</HubertQualifiedSelect.ItemText>
                  <HubertQualifiedSelect.ItemIndicator>
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </HubertQualifiedSelect.ItemIndicator>
                </HubertQualifiedSelect.Item>
              );
            })}
          </HubertQualifiedSelect.Viewport>
          <HubertQualifiedSelect.ScrollDownButton className="flex h-[25px] cursor-default items-center justify-center bg-white text-black">
            <ChevronDown />
          </HubertQualifiedSelect.ScrollDownButton>
        </HubertQualifiedSelect.Content>
      </HubertQualifiedSelect.Portal>
    </SelectPrimitive.Root>
  );
};

interface TriggerProps extends ComponentProps<typeof SelectPrimitive.Trigger> {
  children: ReactNode;
}
const Trigger = ({ children, ...props }: TriggerProps) => {
  return <SelectPrimitive.Trigger {...props}>{children}</SelectPrimitive.Trigger>;
};

interface ValueProps extends ComponentProps<typeof SelectPrimitive.Value> {
  placeholder: string;
  children?: ReactNode;
}
const Value = ({ placeholder, children, ...props }: ValueProps) => {
  return (
    <SelectPrimitive.Value {...props} placeholder={placeholder}>
      {children}
    </SelectPrimitive.Value>
  );
};

interface IconProps extends ComponentProps<typeof SelectPrimitive.Icon> {
  children: ReactNode;
}
const Icon = ({ children, ...props }: IconProps) => {
  return <SelectPrimitive.Icon {...props}>{children}</SelectPrimitive.Icon>;
};

interface PortalProps extends ComponentProps<typeof SelectPrimitive.Portal> {
  children: ReactNode;
}
const Portal = ({ children, ...props }: PortalProps) => {
  return <SelectPrimitive.Portal {...props}>{children}</SelectPrimitive.Portal>;
};

interface ContentProps extends ComponentProps<typeof SelectPrimitive.Content> {
  children: ReactNode;
}
const Content = ({ children, ...props }: ContentProps) => {
  return <SelectPrimitive.Content {...props}>{children}</SelectPrimitive.Content>;
};

interface ScrollUpButtonProps extends ComponentProps<typeof SelectPrimitive.ScrollUpButton> {
  children: ReactNode;
}
const ScrollUpButton = ({ children, ...props }: ScrollUpButtonProps) => {
  return <SelectPrimitive.ScrollUpButton {...props}>{children}</SelectPrimitive.ScrollUpButton>;
};

interface ViewportProps extends ComponentProps<typeof SelectPrimitive.Viewport> {
  children: ReactNode;
}
const Viewport = ({ children, ...props }: ViewportProps) => {
  return <SelectPrimitive.Viewport {...props}>{children}</SelectPrimitive.Viewport>;
};

interface ItemProps extends ComponentProps<typeof SelectPrimitive.Item> {
  className?: string;
  children: ReactNode;
}
const Item = ({ className = '', children, ...props }: ItemProps) => {
  return (
    <SelectPrimitive.Item
      {...props}
      className={clsx(
        'relative',
        'flex items-center',
        'h-[25px]',
        'px-[25px]',
        'rounded-[3px] text-[13px] leading-none select-none',
        // 'data-[disabled]:text-primary data-[disabled]:pointer-events-none',
        // 'data-[highlighted]:outline-none data-[highlighted]:bg-primary-900 data-[highlighted]:text-primary-50',
        className,
      )}
    >
      {children}
    </SelectPrimitive.Item>
  );
};

// const Item = React.forwardRef(({
//   children,
//   className,
//   ...props
// }: ItemProps, forwardedRef) => {
//   return (
//     <SelectPrimitive.Item
//       className={clsx(
//         'text-[13px] leading-none text-violet11 rounded-[3px] flex items-center h-[25px] pr-[35px] pl-[25px] relative select-none data-[disabled]:text-mauve8 data-[disabled]:pointer-events-none data-[highlighted]:outline-none data-[highlighted]:bg-violet9 data-[highlighted]:text-violet1',
//         className
//       )}
//       {...props}
//       ref={forwardedRef}
//     >
//       {/*
//       <Select.ItemText>{children}</Select.ItemText>
//       <Select.ItemIndicator
//         className="absolute left-0 w-[25px] inline-flex items-center justify-center"
//       >
//         <CheckIcon />
//       </Select.ItemIndicator>
//       */}
//       {children}
//     </SelectPrimitive.Item>
//   );
// });

interface ItemTextProps extends ComponentProps<typeof SelectPrimitive.ItemText> {
  children: ReactNode;
}
const ItemText = ({ children, ...props }: ItemTextProps) => {
  return <SelectPrimitive.ItemText {...props}>{children}</SelectPrimitive.ItemText>;
};

interface ItemIndicatorProps extends ComponentProps<typeof SelectPrimitive.ItemIndicator> {
  children: ReactNode;
}
const ItemIndicator = ({ children, ...props }: ItemIndicatorProps) => {
  return <SelectPrimitive.ItemIndicator {...props}>{children}</SelectPrimitive.ItemIndicator>;
};

interface GroupProps extends ComponentProps<typeof SelectPrimitive.Group> {
  children: ReactNode;
}
const Group = ({ children, ...props }: GroupProps) => {
  return <SelectPrimitive.Group {...props}>{children}</SelectPrimitive.Group>;
};

interface LabelProps extends ComponentProps<typeof SelectPrimitive.Label> {
  children: ReactNode;
}
const Label = ({ children, ...props }: LabelProps) => {
  return <SelectPrimitive.Label {...props}>{children}</SelectPrimitive.Label>;
};

interface SeparatorProps extends ComponentProps<typeof SelectPrimitive.Separator> {
  children: ReactNode;
}
const Separator = ({ children, ...props }: SeparatorProps) => {
  return <SelectPrimitive.Separator {...props}>{children}</SelectPrimitive.Separator>;
};

interface ScrollDownButtonProps extends ComponentProps<typeof SelectPrimitive.ScrollDownButton> {
  children: ReactNode;
}
const ScrollDownButton = ({ children, ...props }: ScrollDownButtonProps) => {
  return <SelectPrimitive.ScrollDownButton {...props}>{children}</SelectPrimitive.ScrollDownButton>;
};

interface ArrowProps extends ComponentProps<typeof SelectPrimitive.Arrow> {
  children: ReactNode;
}
const Arrow = ({ children, ...props }: ArrowProps) => {
  return <SelectPrimitive.Arrow {...props}>{children}</SelectPrimitive.Arrow>;
};

HubertQualifiedSelect.Trigger = Trigger;
HubertQualifiedSelect.Value = Value;
HubertQualifiedSelect.Icon = Icon;
HubertQualifiedSelect.Portal = Portal;
HubertQualifiedSelect.Content = Content;
HubertQualifiedSelect.ScrollUpButton = ScrollUpButton;
HubertQualifiedSelect.Viewport = Viewport;
HubertQualifiedSelect.Item = Item;
HubertQualifiedSelect.ItemText = ItemText;
HubertQualifiedSelect.ItemIndicator = ItemIndicator;
HubertQualifiedSelect.Group = Group;
HubertQualifiedSelect.Label = Label;
HubertQualifiedSelect.Separator = Separator;
HubertQualifiedSelect.ScrollDownButton = ScrollDownButton;
HubertQualifiedSelect.Arrow = Arrow;

export default HubertQualifiedSelect;
